import React, { useState, useEffect } from 'react';
import { fastapiService } from '../../services/fastapiService';

export const FastAPIMonitoringPage: React.FC = () => {
  const [logs, setLogs] = useState<string[]>([]);
  const [logFile, setLogFile] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [lines, setLines] = useState(100);
  const [errorsOnly, setErrorsOnly] = useState(false);

  const fetchLogs = async () => {
    setLoading(true);
    try {
      const data = await fastapiService.getFastApiLogs(lines, errorsOnly);
      setLogs(data.logs || []);
      setLogFile(data.file || '');
    } catch (error) {
      console.error('Error fetching FastAPI logs:', error);
      setLogs([`Error: ${error instanceof Error ? error.message : 'Failed to fetch logs'}`]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const data = await fastapiService.getFastApiLogs(lines, errorsOnly);
        if (!cancelled) {
          setLogs(data.logs || []);
          setLogFile(data.file || '');
        }
      } catch (error) {
        if (!cancelled) {
          setLogs([`Error: ${error instanceof Error ? error.message : 'Failed to fetch logs'}`]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [lines, errorsOnly]);

  const getLogLineColor = (line: string) => {
    const lower = line.toLowerCase();
    if (lower.includes('error') || lower.includes('exception') || lower.includes('traceback') || lower.includes('failed')) {
      return 'text-red-400 bg-red-900/20';
    }
    if (lower.includes('warn')) {
      return 'text-yellow-400';
    }
    if (lower.includes('info')) {
      return 'text-blue-400';
    }
    return 'text-gray-300';
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-admin-900">FastAPI Monitoring</h1>
        <p className="mt-1 text-sm text-admin-600">
          View FastAPI backend logs (contact import, API requests, errors)
        </p>
      </div>

      <div className="admin-card p-6">
        <div className="flex flex-wrap items-center justify-between gap-4 mb-4">
          <h2 className="text-lg font-semibold text-admin-900">FastAPI Logs</h2>
          <div className="flex items-center gap-4">
            <label className="flex items-center gap-2 text-sm">
              <span className="text-admin-600">Lines:</span>
              <select
                value={lines}
                onChange={(e) => setLines(Number(e.target.value))}
                className="border border-admin-300 rounded px-2 py-1 text-sm"
              >
                <option value={50}>50</option>
                <option value={100}>100</option>
                <option value={200}>200</option>
                <option value={500}>500</option>
              </select>
            </label>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={errorsOnly}
                onChange={(e) => setErrorsOnly(e.target.checked)}
                className="rounded"
              />
              <span className="text-admin-600">Errors only</span>
            </label>
            <button
              onClick={fetchLogs}
              className="admin-button h-9 px-4 bg-primary-600 text-white hover:bg-primary-700"
            >
              Refresh
            </button>
          </div>
        </div>
        {logFile && (
          <div className="text-xs text-admin-500 mb-2 font-mono truncate" title={logFile}>
            {logFile}
          </div>
        )}
        <div className="bg-gray-900 rounded-lg p-4 h-[32rem] overflow-y-auto font-mono text-sm">
          {loading ? (
            <div className="text-gray-500">Loading logs...</div>
          ) : logs.length === 0 ? (
            <div className="text-gray-500">
              {errorsOnly ? 'No error lines found.' : 'No logs available. Restart FastAPI to start file logging.'}
            </div>
          ) : (
            logs.map((line, index) => (
              <div key={index} className={`mb-1 ${getLogLineColor(line)}`}>
                {line}
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};
